import { useEffect, useSyncExternalStore } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

// One status and one in-flight action per viewer/creator, shared across posts.
const creators = new Map();
function entryFor(viewer, creator) {
  const key = JSON.stringify([viewer, creator]);
  if (!creators.has(key)) creators.set(key, {
    snapshot: { following: false, loaded: false, pending: false, error: '' },
    listeners: new Set(), request: null,
  });
  return creators.get(key);
}
function update(entry, changes) {
  entry.snapshot = { ...entry.snapshot, ...changes };
  entry.listeners.forEach(listener => listener());
}
const requestOptions = (viewer, creator) => ({
  params: { emails: JSON.stringify({ follower_email: viewer, followed_email: creator }) },
  withCredentials: true,
  timeout: 10000,
});
async function load(entry, viewer, creator) {
  if (entry.request || entry.snapshot.loaded) return;
  update(entry, { error: '' });
  entry.request = axios.get(`${API_URL}/api/v1/follow/followStatus`, requestOptions(viewer, creator));
  try {
    const { data } = await entry.request;
    if (!Array.isArray(data)) throw new Error('Invalid follow status');
    update(entry, { following: data.length > 0, loaded: true });
  } catch {
    update(entry, { error: 'Couldn’t check follow status. Try again.' });
  } finally { entry.request = null; }
}
export function useCreatorFollow(viewer, creator) {
  const entry = entryFor(viewer, creator);
  const state = useSyncExternalStore(
    listener => { entry.listeners.add(listener); return () => entry.listeners.delete(listener); },
    () => entry.snapshot,
  );
  const enabled = Boolean(viewer && creator && viewer !== creator);
  useEffect(() => { if (enabled) load(entry, viewer, creator); }, [entry, viewer, creator, enabled]);
  const toggle = async () => {
    if (!enabled || !entry.snapshot.loaded || entry.snapshot.pending) return false;
    const previous = entry.snapshot.following;
    update(entry, { following: !previous, pending: true, error: '' });
    try {
      const options = requestOptions(viewer, creator);
      options.params.following = String(!previous);
      const { data } = await axios.get(`${API_URL}/api/v1/follow/followActivity`, options);
      if (typeof data.following !== 'boolean') throw new Error('Invalid follow response');
      update(entry, { following: data.following, pending: false });
      return true;
    } catch {
      update(entry, { following: previous, pending: false, error: 'Couldn’t save that. Please try again.' });
      return false;
    }
  };
  return { ...state, toggle, retry: () => load(entry, viewer, creator) };
}
